// src/components/charts/RankedBars.tsx
//
// ★★★ WAVE 27 — MAGNITUDE ACROSS NAMED CATEGORIES, which is the plainest job on
// this page and the one most often given to the wrong form.
//
// ⚠ **HORIZONTAL, because the categories are NAMES** — `DUPLICATE_SOURCE_REFERENCE`,
// `ADJUSTMENT_TARGET_UNAVAILABLE`. A column chart would rotate them to 45° or clip
// them, and a rotated label is a label a reader has to work for.
// `StatusBarChart` made the identical choice for the identical reason.
//
// ⚠ **ONE HUE FOR EVERY BAR, and this is the rule that gets broken.** These bars
// are ONE series — a count — across categories, and the category is carried by its
// own label sitting beside it. Colouring each bar differently is the "value-ramp on
// nominal categories" cousin `StatusBarChart`'s header refuses at length: it
// encodes identity twice, spends the palette on nothing, and invites a reader to
// think the colours mean something.
//
// ⚠ **RANKED BY VALUE, DESCENDING — and that is a claim, so it is stated on
// screen.** The caller passes rows in whatever order it has; this sorts them, and
// the frame's subtitle says the order. An unordered bar chart of named categories
// wastes the one thing this form is good at.
//
// ⚠ **NO AXIS.** With the value printed at the end of every bar there is nothing an
// axis adds except ink — `marks-and-anatomy.md`'s "selective direct labels" for the
// case where the labels are not selective at all because there are only a handful.
import type { ReactElement } from 'react'
import { ChartFrame } from './ChartFrame'
import { MarkNoteBubble, MarkNoteLayer, markProps, useMarkNote } from './mark-interaction'

export interface RankedBar {
  readonly label: string
  readonly value: number
  /** Optional link to the rows behind this bar. Omit it and the label is plain text. */
  readonly href?: string
}

export function RankedBars({
  title,
  subtitle,
  bars,
  slot = 1,
  renderLink,
  onSelect,
}: {
  readonly title: string
  readonly subtitle: string
  readonly bars: readonly RankedBar[]
  /** The single hue every bar wears. Defaults to slot 1, the product's own. */
  readonly slot?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  /** Supplied by the caller so this component never imports a router. */
  readonly renderLink?: (bar: RankedBar) => ReactElement
  /**
   * ★★★ Called with the bar the reader pressed — the BAR is the control.
   *
   * ⚠ The label was already a link and the bar beside it was not, which taught a
   * reader that the drawing is decoration and the words are the interface. The
   * label stays a link — it is the accessible, obvious affordance — and the bar
   * now answers a press as well.
   */
  readonly onSelect?: (bar: RankedBar) => void
}): ReactElement {
  const ordered = [...bars].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
  const max = ordered.reduce((highest, bar) => Math.max(highest, bar.value), 0)
  const { note, show, hide } = useMarkNote()

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      columns={[
        { key: 'label', header: 'Reason' },
        { key: 'value', header: 'Count', numeric: true },
      ]}
      rows={ordered.map((bar) => ({ label: bar.label, value: bar.value }))}
      emptyMessage="Nothing in your scope has been refused for any reason."
    >
      <MarkNoteLayer>
      <ul className="pt-ranked-bars">
        {ordered.map((bar) => (
          <li key={bar.label}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm">
                {bar.href !== undefined && renderLink !== undefined ? renderLink(bar) : bar.label}
              </span>
              <span className="pt-tabular text-sm font-semibold">{bar.value.toLocaleString('en-SG')}</span>
            </div>
            {/*
              ⚠ `aria-hidden` — the bar is a picture of the number printed beside
              it, and announcing a meter as well would read the same fact twice.
            */}
            <div
              {...markProps({
                className: 'pt-ranked-track',
                note: { label: bar.label, value: bar.value.toLocaleString('en-SG') },
                ...(onSelect === undefined || bar.href === undefined ? {} : { onSelect: () => onSelect(bar) }),
                show,
                hide,
              })}
              /*
                ⚠ `aria-hidden` SURVIVES when the bar is not pressable — it is then
                a picture of the number printed beside it, and announcing a meter
                as well would read the same fact twice. When it IS pressable it
                has to be reachable, so the hiding is dropped and `markProps`
                supplies the role and the label.
              */
              {...(onSelect !== undefined && bar.href !== undefined ? {} : { 'aria-hidden': true })}
            >
              <div
                className={`pt-ranked-fill pt-series-${slot}`}
                style={{ width: `${max === 0 ? 0 : Math.max(2, (bar.value / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
      <MarkNoteBubble note={note} />
      </MarkNoteLayer>
    </ChartFrame>
  )
}
